import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { ClientAddress, ClientAddressBody, ClientDetail } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Dialog, EmptyState } from '@/components/ui/misc';
import { errorMessage } from '@/features/clients/clients-queries';

/** Botão de ação quieto (lápis/lixeira) usado nas linhas de tabela. */
export function RowIconButton({
  label,
  onClick,
  disabled,
  destructive,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={
        destructive
          ? 'flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-40'
          : 'flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40'
      }
    >
      {children}
    </button>
  );
}

export function ClientAddressesSection({ client }: { client: ClientDetail }) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ address: ClientAddress | null } | null>(null);

  const remove = useMutation({
    mutationFn: async (addressId: number) => {
      const res = await api<void>(`/clients/${client.id}/addresses/${addressId}`, {
        method: 'DELETE',
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['erp', 'clients'] }),
  });

  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="flex items-center justify-between gap-2 p-4 pb-3">
        <h2 className="text-base font-semibold">Endereços</h2>
        <Button size="sm" onClick={() => setDialog({ address: null })}>
          <Plus className="size-4" strokeWidth={2.5} />
          Endereço
        </Button>
      </div>
      {client.addresses.length === 0 ? (
        <EmptyState className="h-28" message="Nenhum endereço cadastrado" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-9 px-4 font-semibold">CEP</th>
                <th className="h-9 px-4 font-semibold">Logradouro</th>
                <th className="h-9 px-4 font-semibold">Bairro</th>
                <th className="h-9 px-4 font-semibold">Cidade</th>
                <th className="h-9 px-4 font-semibold">UF</th>
                <th className="h-9 w-20 px-4">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {client.addresses.map((a) => (
                <tr
                  key={a.id}
                  className="h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                >
                  <td className="px-4 whitespace-nowrap tabular-nums">{a.cep ?? '—'}</td>
                  <td className="max-w-56 truncate px-4">
                    {[a.street, a.number].filter(Boolean).join(', ') || '—'}
                    {a.complement ? (
                      <span className="text-muted-foreground"> — {a.complement}</span>
                    ) : null}
                  </td>
                  <td className="max-w-40 truncate px-4">{a.neighborhood ?? '—'}</td>
                  <td className="max-w-40 truncate px-4">{a.city ?? '—'}</td>
                  <td className="px-4 whitespace-nowrap">{a.state ?? '—'}</td>
                  <td className="px-4">
                    <div className="flex justify-end gap-0.5">
                      <RowIconButton label="Editar endereço" onClick={() => setDialog({ address: a })}>
                        <Pencil className="size-3.5" />
                      </RowIconButton>
                      <RowIconButton
                        label="Remover endereço"
                        destructive
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm('Remover este endereço?')) remove.mutate(a.id);
                        }}
                      >
                        <Trash2 className="size-3.5" />
                      </RowIconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {remove.error ? (
        <p className="px-4 pb-3 text-sm text-destructive">{errorMessage(remove.error)}</p>
      ) : null}
      {dialog ? (
        <AddressDialog
          clientId={client.id}
          address={dialog.address}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </section>
  );
}

function AddressDialog({
  clientId,
  address,
  onClose,
}: {
  clientId: number;
  address: ClientAddress | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [cep, setCep] = useState(address?.cep ?? '');
  const [street, setStreet] = useState(address?.street ?? '');
  const [number, setNumber] = useState(address?.number ?? '');
  const [complement, setComplement] = useState(address?.complement ?? '');
  const [neighborhood, setNeighborhood] = useState(address?.neighborhood ?? '');
  const [city, setCity] = useState(address?.city ?? '');
  const [state, setState] = useState(address?.state ?? '');

  const save = useMutation({
    mutationFn: async (body: ClientAddressBody) => {
      const res = address
        ? await api<ClientAddress>(`/clients/${clientId}/addresses/${address.id}`, {
            method: 'PUT',
            body,
          })
        : await api<ClientAddress>(`/clients/${clientId}/addresses`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'clients'] });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate({
      cep: cep.trim() || null,
      street: street.trim() || null,
      number: number.trim() || null,
      complement: complement.trim() || null,
      neighborhood: neighborhood.trim() || null,
      city: city.trim() || null,
      state: state.trim() || null,
    });
  };

  return (
    <Dialog open onClose={onClose} title={address ? 'Editar endereço' : 'Novo endereço'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="addr-cep">CEP</Label>
            <Input id="addr-cep" value={cep} onChange={(e) => setCep(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="addr-number">Número</Label>
            <Input id="addr-number" value={number} onChange={(e) => setNumber(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="addr-street">Logradouro</Label>
          <Input id="addr-street" value={street} onChange={(e) => setStreet(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="addr-complement">Complemento</Label>
          <Input
            id="addr-complement"
            value={complement}
            onChange={(e) => setComplement(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="addr-neighborhood">Bairro</Label>
          <Input
            id="addr-neighborhood"
            value={neighborhood}
            onChange={(e) => setNeighborhood(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="addr-city">Cidade</Label>
            <Input id="addr-city" value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="addr-state">UF</Label>
            <Input
              id="addr-state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              maxLength={2}
            />
          </div>
        </div>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
